// src/components/inventory/InventoryHistoryTab.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Live: new ledger entries arrive through the WebSocket (inventory_update) and are
//     highlighted; live status pill with the last change.
//   • Download CSV works (all filtered rows); the Filters button opens real filters: date
//     range, officer, stock in / out, plus types taken from the data (the server writes
//     Restock / Issue / Return / Cycle Count / Adjustment / Transfer / Initial, so the old
//     Stock In / Stock Out / Correction options never matched — they are kept as well).
//   • Paging (50 rows) so a long ledger stays fast.
//   • FlowSense AI summary: in / out / net, rate, corrections, most active officers, most
//     consumed items; "Explain this screen" and the Ministry of Works explanation.
//   • Removed invented claims ("System Reliability 100%", "ISO-27001 audit ready",
//     "encrypted protocol"); error state with Retry.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, useToast, useColorModeValue, Spinner,
  Text, Flex, Badge, InputGroup, InputLeftElement, Input, Icon, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, StatArrow, Button, HStack,
  VStack, Select, Tooltip, IconButton, Divider, Tag, Avatar, TagLabel, TagLeftIcon
} from '@chakra-ui/react';
import { Collapse, Alert, AlertIcon, FormControl, FormLabel } from '@chakra-ui/react'; // --- UPDATE
import {
  MdSearch, MdHistory, MdFileDownload, MdTrendingUp, MdTrendingDown,
  MdFilterList, MdDateRange, MdSecurity, MdSwapVert, MdAutoGraph, MdLayers
} from 'react-icons/md';
import { MdUndo, MdFactCheck, MdSwapHoriz, MdChevronLeft, MdChevronRight } from 'react-icons/md'; // --- UPDATE
import { motion, AnimatePresence } from 'framer-motion';
import inventoryService from '../../services/inventoryService';
// --- UPDATE (MOW+ v2)
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { summarizeHistory, historyDirection } from './inventoryAiIntel';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';
const PAGE = 50;
// --- END UPDATE

// Animation variants for production-grade feel
const MotionBox = motion(Box);
const MotionTr = motion(Tr);

const InventoryHistoryTab = ({ intel = null, command = null }) => { // --- UPDATE: + intel (AI overview, optional), command (quick actions)
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  // --- UPDATE
  const [showFilters, setShowFilters] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [officer, setOfficer] = useState('all');
  const [direction, setDirection] = useState('all');
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [freshIds, setFreshIds] = useState(() => new Set());
  const knownIds = React.useRef(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);
  // --- END UPDATE
  const toast = useToast();

  // --- HOOKS DECLARED AT TOP LEVEL (FIXED) ---
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const headerBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const secondaryText = useColorModeValue("gray.600", "gray.400");
  const tableHoverBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const accentGradient = "linear(to-r, blue.500, cyan.400)";
  const statBg = useColorModeValue("white", "gray.900");
  const itemColor = useColorModeValue("blue.600", "blue.300"); // --- UPDATE: readable in dark mode

  useEffect(() => {
    fetchHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = async (quiet = false) => { // --- UPDATE: + quiet (live refresh without spinner)
    if (!quiet) setIsLoading(true);
    try {
      const data = await inventoryService.getCompleteHistory();
      // --- UPDATE: highlight entries that arrived live
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      if (!mountedRef.current) return;
      if (knownIds.current) {
        const fresh = new Set(list.filter((e) => !knownIds.current.has(e.id)).map((e) => e.id));
        if (fresh.size) { setFreshIds(fresh); setTimeout(() => { if (mountedRef.current) setFreshIds(new Set()); }, 4000); }
      }
      knownIds.current = new Set(list.map((e) => e.id));
      setHistory(list);
      setError(null); live.markUpdated();
      // --- END UPDATE
    } catch (error) {
      setError(error?.message || 'Connection to the ledger was interrupted.'); // --- UPDATE
      if (!quiet) toast({
        title: 'Audit Sync Failed',
        description: 'Connection to the secure ledger was interrupted.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  };

  // --- UPDATE: live ledger
  const live = useInventoryLive(() => fetchHistory(true));
  // --- END UPDATE

  // Enterprise Search Logic: Multi-field matching
  const filteredData = useMemo(() => {
    // --- UPDATE: date range / officer / direction
    const fromT = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toT = to ? new Date(`${to}T23:59:59`).getTime() : null;
    // --- END UPDATE
    return history.filter(entry => {
      const content = `${entry.item_name} ${entry.user_name} ${entry.reason}`.toLowerCase();
      const matchesSearch = content.includes(searchTerm.toLowerCase());
      const matchesAction = filterAction === "All" || entry.change_type === filterAction;
      // --- UPDATE
      const t = entry.timestamp ? new Date(entry.timestamp).getTime() : null;
      const matchesDate = (fromT === null || (t !== null && t >= fromT)) && (toT === null || (t !== null && t <= toT));
      const matchesOfficer = officer === 'all' || (entry.user_name || 'system') === officer;
      const matchesDir = direction === 'all' || historyDirection(entry) === direction;
      return matchesSearch && matchesAction && matchesDate && matchesOfficer && matchesDir;
      // --- END UPDATE
    });
  }, [history, searchTerm, filterAction, from, to, officer, direction]);

  // Analytics Calculation for the Header Stats
  const analytics = useMemo(() => {
    if (!history.length) return { incoming: 0, outgoing: 0, count: 0 };
    return history.reduce((acc, curr) => {
      if (curr.quantity_change > 0) acc.incoming += curr.quantity_change;
      else acc.outgoing += Math.abs(curr.quantity_change);
      return acc;
    }, { incoming: 0, outgoing: 0, count: history.length });
  }, [history]);

  // --- UPDATE: FlowSense summary, filter options, paging, export
  const summary = useMemo(() => summarizeHistory(filteredData), [filteredData]);
  const typeOptions = useMemo(() => Array.from(new Set([...history.map((h) => h.change_type).filter(Boolean), 'Stock In', 'Stock Out', 'Correction'])), [history]);
  const officers = useMemo(() => Array.from(new Set(history.map((h) => h.user_name || 'system'))).sort(), [history]);
  useEffect(() => { setPage(1); }, [searchTerm, filterAction, from, to, officer, direction]);
  const pages = Math.max(1, Math.ceil(filteredData.length / PAGE));
  const rows = filteredData.slice((page - 1) * PAGE, page * PAGE);
  const activeFilters = [from, to].filter(Boolean).length + (officer !== 'all' ? 1 : 0) + (direction !== 'all' ? 1 : 0);
  const clearFilters = () => { setFrom(''); setTo(''); setOfficer('all'); setDirection('all'); setFilterAction('All'); setSearchTerm(''); };
  const exportCsv = () => {
    const n = downloadCsv(`flowsense-audit-history-${stamp()}.csv`, ['timestamp', 'item', 'type', 'quantity_change', 'officer', 'reason'],
      filteredData.map((e) => [e.timestamp ? new Date(e.timestamp).toISOString() : '', e.item_name || '', e.change_type || '', e.quantity_change, e.user_name || '', e.reason || '']));
    toast({ title: 'Audit history exported', description: `${n} rows`, status: 'success', duration: 2000 });
  };
  // --- END UPDATE

  // --- UPDATE: quick action "Export Audit Log"
  useEffect(() => { if (command?.type === 'exportAudit' && history.length) exportCsv(); }, [command]); // eslint-disable-line react-hooks/exhaustive-deps

  const getActionTheme = (type) => {
    switch (type) {
      case 'Stock In': return { color: 'green', icon: MdTrendingUp, label: 'Ingress' };
      case 'Stock Out': return { color: 'red', icon: MdTrendingDown, label: 'Egress' };
      case 'Correction': return { color: 'orange', icon: MdSwapVert, label: 'Audit Adj.' };
      case 'Initial': return { color: 'blue', icon: MdHistory, label: 'Provision' };
      // --- UPDATE: the types the server writes
      case 'Restock': return { color: 'green', icon: MdTrendingUp, label: 'Restock' };
      case 'Issue': return { color: 'red', icon: MdTrendingDown, label: 'Issue' };
      case 'Return': return { color: 'teal', icon: MdUndo, label: 'Return' };
      case 'Adjustment': return { color: 'orange', icon: MdSwapVert, label: 'Adjustment' };
      case 'Cycle Count': return { color: 'purple', icon: MdFactCheck, label: 'Cycle count' };
      case 'Transfer': return { color: 'blue', icon: MdSwapHoriz, label: 'Transfer' };
      // --- END UPDATE
      default: return { color: 'gray', icon: MdLayers, label: type || 'Update' }; // --- UPDATE: show the real type
    }
  };

  return (
    <Box p={2}>
      {/* 1. Dashboard Branding & Controls */}
      <MotionBox initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <Flex justify="space-between" align="center" mb={6} gap={3} flexWrap="wrap">
          <Box>
            <HStack spacing={2} color="blue.500" mb={1}>
              <Icon as={MdSecurity} />
              <Text fontWeight="bold" fontSize="xs" letterSpacing="2px">SYSTEM AUDIT LEDGER</Text>
            </HStack>
            <Text fontSize="3xl" fontWeight="black" letterSpacing="tight">
              Transaction History
            </Text>
          </Box>

          <HStack spacing={3}>
            <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} />{/* --- UPDATE */}
            <Button leftIcon={<MdFileDownload />} colorScheme="blue" variant="ghost" borderRadius="xl" onClick={exportCsv} isDisabled={!filteredData.length}>{/* --- UPDATE: wired */}
              Download CSV
            </Button>
            <IconButton
              icon={<MdHistory />}
              aria-label="Refresh Ledger"
              onClick={() => fetchHistory()}
              borderRadius="xl"
              isLoading={isLoading}
            />
          </HStack>
        </Flex>

        {/* --- UPDATE: FlowSense AI */}
        <Box mb={6}>
          <FlowSenseInsightBar screen="history" data={intel} headline={summary.count ? summary.lines.slice(0, 2) : 'No ledger entries match yet.'} extra={{ summary: { ...summary, largest: undefined }, filters: { from, to, officer, direction, type: filterAction } }}
            rulesAnswer={(q) => (/summar|ledger|history|unusual|correction|who|officer|consum/i.test(q) ? ['Audit history — FlowSense summary of the rows shown:', ...summary.lines].join('\n') : null)}
            questions={['Summarise the audit history', 'Any unusual corrections?', 'What will run out first?', 'Explain the inventory system for the Ministry of Works']} />
        </Box>
        {error && <Alert status="error" borderRadius="xl" mb={6}><AlertIcon />{error}<Button size="sm" ml="auto" onClick={() => fetchHistory()}>Retry</Button></Alert>}
        {/* --- END UPDATE */}

        {/* 2. Enterprise Stats Grid */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
          <Stat p={5} bg={statBg} borderRadius="2xl" borderLeft="4px solid" borderColor="green.400" boxShadow="sm">
            <StatLabel color={secondaryText} fontWeight="bold" fontSize="xs">TOTAL INBOUND VOLUME</StatLabel>
            <StatNumber fontSize="3xl" fontWeight="black">+{analytics.incoming}</StatNumber>
            <StatHelpText><StatArrow type="increase" /> Active Logistics</StatHelpText>
          </Stat>
          <Stat p={5} bg={statBg} borderRadius="2xl" borderLeft="4px solid" borderColor="red.400" boxShadow="sm">
            <StatLabel color={secondaryText} fontWeight="bold" fontSize="xs">TOTAL OUTBOUND VOLUME</StatLabel>
            <StatNumber fontSize="3xl" fontWeight="black">-{analytics.outgoing}</StatNumber>
            <StatHelpText><StatArrow type="decrease" /> Consumption Rate</StatHelpText>
          </Stat>
          <Stat p={5} bg={statBg} borderRadius="2xl" borderLeft="4px solid" borderColor="blue.400" boxShadow="sm">
            <StatLabel color={secondaryText} fontWeight="bold" fontSize="xs">LOG ENTRIES</StatLabel>
            <StatNumber fontSize="3xl" fontWeight="black">{analytics.count}</StatNumber>
            {/* --- UPDATE: real activity rate (was "System Reliability 100%") */}
            <StatHelpText>{(() => { const s = summarizeHistory(history); return s.perDay != null ? `${s.perDay}/day over ${s.spanDays} days` : 'No activity yet'; })()}</StatHelpText>
          </Stat>
        </SimpleGrid>
      </MotionBox>

      {/* 3. Search & Advanced Filtering */}
      <Flex bg={bgColor} p={4} borderRadius="2xl" mb={showFilters ? 2 : 6} gap={4} borderWidth="1px" borderColor={borderColor}>
        <InputGroup flex={4}>
          <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
          <Input
            placeholder="Search by part name, operator ID, or action details..."
            variant="unstyled"
            pl={10}
            fontSize="sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search audit history" // --- UPDATE
          />
        </InputGroup>
        <Divider orientation="vertical" h="24px" my="auto" />
        <Select
          variant="unstyled"
          flex={1}
          fontSize="sm"
          fontWeight="bold"
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          aria-label="Action filter" // --- UPDATE
        >
          <option value="All">All Actions</option>
          {/* --- UPDATE: real types + the original options */}
          {typeOptions.map((t) => <option key={t} value={t}>{t === 'Correction' ? 'Corrections' : t}</option>)}
        </Select>
        <Button leftIcon={<MdFilterList />} variant={showFilters || activeFilters ? 'solid' : 'ghost'} colorScheme={activeFilters ? 'blue' : undefined} size="sm" borderRadius="lg" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}>{/* --- UPDATE: wired */}
          Filters{activeFilters ? ` (${activeFilters})` : ''}
        </Button>
      </Flex>
      {/* --- UPDATE: advanced filters */}
      <Collapse in={showFilters} animateOpacity>
        <Flex bg={bgColor} p={4} borderRadius="2xl" mb={6} gap={4} borderWidth="1px" borderColor={borderColor} flexWrap="wrap" align="flex-end">
          <FormControl maxW="170px"><FormLabel fontSize="xs"><Icon as={MdDateRange} mr={1} />From</FormLabel><Input size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" /></FormControl>
          <FormControl maxW="170px"><FormLabel fontSize="xs">To</FormLabel><Input size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" /></FormControl>
          <FormControl maxW="200px"><FormLabel fontSize="xs">Officer</FormLabel><Select size="sm" value={officer} onChange={(e) => setOfficer(e.target.value)} aria-label="Officer filter"><option value="all">All officers</option>{officers.map((o) => <option key={o} value={o}>{o}</option>)}</Select></FormControl>
          <FormControl maxW="180px"><FormLabel fontSize="xs">Direction</FormLabel><Select size="sm" value={direction} onChange={(e) => setDirection(e.target.value)} aria-label="Direction filter"><option value="all">In & out</option><option value="in">Stock in</option><option value="out">Stock out</option><option value="zero">No quantity change</option></Select></FormControl>
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear all</Button>
          <Text fontSize="xs" color={secondaryText} ml="auto">{filteredData.length} of {history.length} entries</Text>
        </Flex>
      </Collapse>
      {/* --- END UPDATE */}

      {/* 4. The Main Data Table */}
      <Box
        bg={bgColor}
        borderRadius="2xl"
        boxShadow="xl"
        overflow="hidden"
        borderWidth="1px"
        borderColor={borderColor}
      >
        {isLoading ? (
          <Flex direction="column" align="center" justify="center" p={40}>
            <Spinner size="xl" color="blue.500" thickness="4px" speed="0.8s" />
            <Text mt={4} fontWeight="bold" color={secondaryText} letterSpacing="wider">SECURE DATA SYNC...</Text>
          </Flex>
        ) : filteredData.length === 0 ? (
          <Flex direction="column" align="center" justify="center" p={20}>
            <Icon as={MdLayers} boxSize={12} color="gray.200" mb={4} />
            <Text color={secondaryText} fontWeight="medium">No matching history found in the ledger.</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">{/* --- UPDATE: scrolls on small screens */}
          <Table variant="simple" aria-label="Audit history">
            <Thead bg={headerBg}>
              <Tr>
                <Th py={5} color={secondaryText}>Timeline</Th>
                <Th color={secondaryText}>Inventory Asset</Th>
                <Th color={secondaryText}>Authorized Operator</Th>
                <Th color={secondaryText}>Action</Th>
                <Th isNumeric color={secondaryText}>Quantity Delta</Th>
                <Th color={secondaryText}>Technical Reason</Th>
              </Tr>
            </Thead>
            <Tbody>
              <AnimatePresence>
                {rows.map((entry, index) => { /* --- UPDATE: paged */
                  const theme = getActionTheme(entry.change_type);
                  const isFresh = freshIds.has(entry.id); // --- UPDATE
                  return (
                    <MotionTr
                      key={entry.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isFresh ? { opacity: 1, x: 0, backgroundColor: ['rgba(56,178,172,0.35)', 'rgba(56,178,172,0)'] } : { opacity: 1, x: 0 }} // --- UPDATE
                      transition={isFresh ? { duration: 3 } : { delay: Math.min(index * 0.02, 0.5) }}
                      _hover={{ bg: tableHoverBg }}
                      data-fresh={isFresh ? 'true' : undefined}
                    >
                      <Td borderBottomWidth="1px" borderColor={borderColor}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" fontSize="sm">{new Date(entry.timestamp).toLocaleDateString()}</Text>
                          <Text fontSize="xs" color={secondaryText}>{new Date(entry.timestamp).toLocaleTimeString()}</Text>
                        </VStack>
                      </Td>
                      <Td borderBottomWidth="1px" borderColor={borderColor}>
                        <HStack>
                          <Icon as={MdAutoGraph} color="blue.500" />
                          <Text fontWeight="black" color={itemColor} fontSize="sm">{entry.item_name}</Text>
                          {isFresh && <Badge colorScheme="teal" fontSize="2xs">new</Badge>}{/* --- UPDATE */}
                        </HStack>
                      </Td>
                      <Td borderBottomWidth="1px" borderColor={borderColor}>
                        <HStack>
                          <Avatar size="xs" name={entry.user_name} />
                          <Text fontSize="sm" fontWeight="semibold">{entry.user_name || 'system'}</Text>
                        </HStack>
                      </Td>
                      <Td borderBottomWidth="1px" borderColor={borderColor}>
                        <Tag colorScheme={theme.color} borderRadius="full" variant="subtle" size="md">
                          <TagLeftIcon as={theme.icon} />
                          <TagLabel fontWeight="bold">{theme.label}</TagLabel>
                        </Tag>
                      </Td>
                      <Td isNumeric borderBottomWidth="1px" borderColor={borderColor}>
                        <Text
                          fontWeight="black"
                          fontSize="md"
                          color={entry.quantity_change > 0 ? 'green.500' : entry.quantity_change < 0 ? 'red.500' : secondaryText} // --- UPDATE
                        >
                          {entry.quantity_change > 0 ? `+${entry.quantity_change}` : entry.quantity_change}
                        </Text>
                      </Td>
                      <Td borderBottomWidth="1px" borderColor={borderColor}>
                        <Tooltip label={entry.reason} hasArrow placement="top">
                          <Text fontSize="xs" color={secondaryText} noOfLines={1} maxW="200px" fontStyle="italic">
                            "{entry.reason || "Automatic system adjustment."}"
                          </Text>
                        </Tooltip>
                      </Td>
                    </MotionTr>
                  );
                })}
              </AnimatePresence>
            </Tbody>
          </Table>
          </Box>
        )}
        {/* --- UPDATE: paging */}
        {!isLoading && pages > 1 && (
          <HStack justify="flex-end" px={4} py={2} borderTopWidth="1px" borderColor={borderColor}>
            <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronLeft} />} isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
            <Text fontSize="xs">Page {page} / {pages}</Text>
            <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronRight} />} isDisabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
          </HStack>
        )}
      </Box>

      {/* Footer Audit Compliance */}
      <Flex mt={8} justify="center" opacity={0.5}>
        <HStack spacing={4}>
          {/* --- UPDATE: factual footer (was "ENCRYPTED PROTOCOL · ISO-27001 AUDIT READY · FLOWSENSE CORE v4.0") */}
          <Text fontSize="xs" fontWeight="bold">FLOWSENSE MOW+ AUDIT LEDGER</Text>
          <Divider orientation="vertical" h="10px" />
          <Text fontSize="xs" fontWeight="bold">{history.length} ENTRIES</Text>
          <Divider orientation="vertical" h="10px" />
          <Text fontSize="xs" fontWeight="bold">{live.connected === false ? 'AUTO-REFRESH' : 'LIVE UPDATES'}</Text>
        </HStack>
      </Flex>
    </Box>
  );
};

export default InventoryHistoryTab;
